import { useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { assetUrl, runtimeLabel } from "../lib/catalog";
import { currentAccess } from "../services/api";
import type { Title } from "../types";
import * as I from "./Icons";

const CATEGORY_LABEL = { movie: "Película", series: "Serie", anime: "Anime" } as const;

export function Art({ k, alt, className = "", eager = false }: { k?: string; alt: string; className?: string; eager?: boolean }) {
  const src = assetUrl(currentAccess(), k);
  if (!src) {
    return (
      <div className={`grid place-items-center bg-[radial-gradient(circle_at_30%_20%,#2a2232,#13131b)] p-3 text-center ${className}`}>
        <span className="font-display text-lg text-muted">{alt}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" className={`object-cover ${className}`} />;
}

export function PosterCard({ t, progress }: { t: Title; progress?: number }) {
  const eps = t.kind === "series" ? (t.seasons ?? []).reduce((n, s) => n + s.episodes.length, 0) : 0;
  return (
    <Link to={`/titulo/${t.id}`} className="poster group block w-[42vw] shrink-0 sm:w-[200px] xl:w-[220px]" data-testid="poster">
      <div className="relative aspect-[2/3] overflow-hidden rounded-lg bg-surface">
        <Art k={t.poster ?? t.backdrop} alt={t.title} className="h-full w-full" />
        {progress !== undefined && (
          <div className="absolute inset-x-2 bottom-2 h-1 rounded-full bg-black/60">
            <div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(100, progress * 100)}%` }} />
          </div>
        )}
      </div>
      <p className="mt-2 truncate text-[15px] font-medium">{t.title}</p>
      <p className="truncate text-sm text-muted">
        {[t.year, t.kind === "series" ? `${eps} episodios` : t.media ? runtimeLabel(t.media.durationSec) : CATEGORY_LABEL[t.category]].filter(Boolean).join("  ")}
      </p>
    </Link>
  );
}

export function Row({ title, children, link }: { title: string; children: ReactNode; link?: { to: string; label: string } }) {
  const ref = useRef<HTMLDivElement>(null);
  const scroll = (dir: number) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.85, behavior: "smooth" });
  return (
    <section className="mt-10" aria-label={title}>
      <div className="mb-3 flex items-baseline gap-4">
        <h2 className="font-display text-2xl">{title}</h2>
        {link && <Link to={link.to} className="text-sm text-muted hover:text-text">{link.label}</Link>}
        <div className="ml-auto hidden gap-1 md:flex">
          <button className="icon-btn" onClick={() => scroll(-1)} aria-label={`Anterior en ${title}`}><I.ArrowLeft /></button>
          <button className="icon-btn" onClick={() => scroll(1)} aria-label={`Siguiente en ${title}`}><I.ChevronRight /></button>
        </div>
      </div>
      <div ref={ref} className="row-scroll -mx-4 flex gap-3 overflow-x-auto px-4 pb-2 sm:-mx-8 sm:gap-4 sm:px-8 lg:-mx-12 lg:px-12">
        {children}
      </div>
    </section>
  );
}

export function Grid({ items }: { items: Title[] }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] sm:gap-x-4">
      {items.map((t) => (
        <div key={t.id} className="[&>a]:w-full">
          <PosterCard t={t} />
        </div>
      ))}
    </div>
  );
}

export function Notice({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mx-auto max-w-xl py-24 text-center">
      <h1 className="font-display text-3xl">{title}</h1>
      <div className="mt-3 text-muted">{children}</div>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
